/**
 * @Copyright 2014-2017 DataVoke, Inc. All rights reserved.
 */

import * as dynamicValue from '../entities/dynamicValue';
import * as GridValue from "./gridValue";
import * as tools from '../utils/tools';
import {
    EntityStates,
    gridRecordEvents
} from '../utils/dvtypes';
import helpers from '../helpers/helpers';

"use strict";

//namespace elements
export const gridValue = GridValue;

// Calls updateUI on all child gridValue objects...AL
export function updateUI (gr, gv) {
    if (gr.parentMVC != null && gr.parentMVC.bulkInsert) {
        // do not update UI during bulkinserts...WR
        return;
    }

    // loop through the grid values and ensure they updateUI() as well.
    for (let i = 0, l = gr.gridValues.Values.length; i < l; i++) {
        let rgv = gr.gridValues.Values[i];
        if (gv !== rgv) { rgv.updateUI(); }
    }
}

export function isValidForSave (gr) {
    // if no MVC, no saving.
    if (gr.parentMVC == null) { return false; }
    let errors = [], recordCodePropertyID = gr.parentMVC.primaryRecordType.Definition.CodePropertyID;

    // Must have record code...AL
    if (gr.clientRecord.Record.Code == null || gr.clientRecord.Record.Code === "") {
        errors.push(helpers.error.create(
            gr.parentMVC.drivingView.ViewID,
            gr.clientRecord.Record.RecordID,
            null,  // No property id to record (TODO: maybe we should? -- current SL version does not do this)..AL
            gr.gridValues.getVal(recordCodePropertyID).property.Definition.Name,
            'Required Key'
            ));
    }

    // Evaluate each property for this record for requirements...AL
    let currGC, currGV;
    for (let i = 0, l = gr.parentMVC.gridColumns.Values.length; i < l; i++) {
        currGC = gr.parentMVC.gridColumns.Values[i];
        currGV = gr.gridValues.getVal(currGC.property.PropertyID);

        // logic for determining if the gv is required, but has no value in the gridValue object.
        //NOTE: we only need to notify about the record code property once.
        if (recordCodePropertyID != null
            && currGV.property.UID !== recordCodePropertyID.UID
            && currGV.isRequiredFieldEmpty()) {
            errors.push(helpers.error.create(
                gr.parentMVC.drivingView.ViewID,
                gr.clientRecord.Record.RecordID,
                currGC.property.PropertyID,
                currGC.property.Definition.Name,
                'Required'
            ));
        }

        // ensure that each value is correct for the datatype.
        let validation = tools.validateValue(currGV.dynamicValue.DataType, currGV.value);
        if (!validation || !validation.response) {
            errors.push(helpers.error.create(
                gr.parentMVC.drivingView.ViewID,
                gr.clientRecord.Record.RecordID,
                currGC.property.PropertyID,
                currGC.property.Definition.Name,
                'Invalid Data'
            ));
        }
    }

    return errors;
}

export function addClientRecordReference (gr) {
    let locals = gr.clientRecord.localRecords,
        foundValues = [];
    for (let i = locals.length - 1; i >= 0; i--) {
        if (locals[i].parentMVC === gr.parentMVC) {
            // we found an exact reference match; this record is already a local record!
            // remove it from the client record as we will replace it (due to values, etc.).
            let t = locals.splice(i, 1);
            if (t != null) {
                foundValues.concat(t);
            }
            break;
        }
    }
    if (foundValues.length > 0) {
        return foundValues;
    }
}

export function setPivotGridValue (gr, value, pivotPropertyID) {
    let dv = dynamicValue.DynamicValue(value, app.dv.cache.properties.allProperties.getVal(pivotPropertyID), gr.clientRecord.RecordID, null, 0);
    // set state to unmodified and remove the superfluous edit value.
    dv._State = EntityStates.Unmodified;
    dv.dynamicValueEdit = null;
    return gr.gridValues.add(pivotPropertyID, gridValue.create(dv, pivotPropertyID, gr));
}

/**
 * Add references for the records used to generate a pivot value to the record. It will be put into
 * a dictionary under the property ID of the value calculated.
 */
export function setPivotRecordsForValue (gr, records, pivotPropertyID) {
    if (gr.pivotRecords == null) {
        gr.pivotRecords = new tools.dictionary();
    }
    return gr.pivotRecords.add(pivotPropertyID, records);
}

export function isDirty (gr) {
    let ret = false;
    // we are dirty if we are a new, unsaved record.
    //Added a null check for analysis views...BC
    if (gr.clientRecord != null && gr.clientRecord.Record._State !== EntityStates.Unmodified) {
        ret = true;
    }
    else {
        // we are dirty if any of the grid values are dirty.
        for (let i = 0, il = gr.gridValues.Values.length; i < il; i++) {
            let gv = gr.gridValues.Values[i];
            if (gv.isDirty()) {
                ret = true;
                break;
            }
        }
    }
    return ret;
}

export function getRecordStatus (gr) {
    if (gr.clientRecord.Record.Status == null) {
        return app.dv.cache.valueStatuses.notInitializedStatus
    }
    else {
        return app.dv.cache.valueStatuses.valueStatusByValue.getVal(gr.clientRecord.Record.Status);
    }
}

export function setRecordStatus (gr, newStatus) {
    if (gr.clientRecord.Record == null ||
        (gr.clientRecord.Record != null && gr.clientRecord.Record.Status === newStatus.StatusValue)) {

        // if there is no status or no change in status, then do nothing.
        return;
    }

    // Set the status value...AL
    gr.clientRecord.Record.Status = newStatus.StatusValue;


    if (gr.clientRecord.Record._State !== EntityStates.NewObject) {
        gr.clientRecord.Record._State = EntityStates.Modified;
    }

    // Make sure to call update for all the child statuses (this could be a record status)...AL
    for (let i = 0, l = gr.gridValues.Values.length; i < l; i++) {
        gr.gridValues.Values[i].updateUI();
    }

    // raise event.
    gr._.runSubCallbacks(gr, gridRecordEvents.StatusChanged);
}

export function getRecordState (gr) {
    let stat = null;
    for (let i = 0, l = gr.gridValues.Values.length; i < l; i++) {
        switch (gr.gridValues.Values[i].dynamicValue._State) {
            case EntityStates.Deleted:
                // this overrides all other states.
                stat = EntityStates.Deleted;
                break;
            case EntityStates.NewObject:
                switch (stat) {
                    case EntityStates.Deleted:
                        // do not change the returned state.
                        break;
                    default:
                        stat = EntityStates.NewObject;
                        break;
                }
                break;
            case EntityStates.Modified:
                switch (stat) {
                    case EntityStates.Modified:
                        // do not change the returned state.
                        break;
                    default:
                        stat = EntityStates.Modified;
                        break;
                }
                break;
            case EntityStates.Unmodified:
            default:
                switch (stat) {
                    case EntityStates.Deleted:
                    case EntityStates.NewObject:
                    case EntityStates.Modified:
                        // do not change the returned state.
                        break;
                    default:
                        stat = EntityStates.Unmodified;
                        break;
                }
                break;
        }
    }
    return stat;
}

export function calculateGridValue (gr, propertyIDs, formula) {
    if (propertyIDs == null || propertyIDs.length === 0) return null;

    let valArr = [];

    for (let i = 0, il = propertyIDs.length; i < il; i++) {
        let currVal = gr.gridValues.getVal(propertyIDs[i]);
        if (currVal != null && currVal.dynamicValue != null && currVal.dynamicValue.Value != null) {
            valArr.push(currVal.dynamicValue.Value);
        }
        else {
            valArr.push("");
        }
    }

    return String.format(formula, valArr);
}

export function dispose (gr) {
    let list = gr.gridValues.Values;
    for (let i = list.length - 1; 0 <= i; i--) {
        let o = list[i];
        o.dispose();

        // remove circlular reference for the client value.
        let indexOf = o.clientValue.localValues.indexOf(o);
        if (indexOf > -1) {
            o.clientValue.localValues.splice(indexOf, 1);
        }
        o.clientValue = null;
    }

    tools.clearSubscriptions(gr._.subs, gridRecordEvents.StatusChanged);

    gr.gridValues.clear();
    let index = gr.clientRecord.localRecords.indexOf(gr);
    if (index !== -1) {
        gr.clientRecord.localRecords.splice(index, 1);
    }
    gr.clientRecord = null;
    gr.parentMVC = null;

    if (gr.apiRef != null && gr.apiRef.dispose != null) {
        gr.apiRef.dispose();
    }

    gr.isDisposed = true;
}

export function createLinkShell (gr) {
    let newObj = {};
    newObj.linkShellFlag = true;
    newObj.clientRecord = {};
    newObj.clientRecord.Record = {};
    newObj.clientRecord.Record.RecordID = gr.clientRecord.Record.RecordID;
    newObj.clientRecord.Record.Code = gr.clientRecord.Record.Code;
    newObj.clientRecord.Record.RecordTypeID = gr.clientRecord.Record.RecordTypeID;
    newObj.clientRecord.Record.UID = gr.clientRecord.Record.UID;
    newObj.clientRecord.Record.Status = gr.clientRecord.Record.Status;
    newObj.parentMVC = gr.parentMVC;
    return newObj;
}

export class GridRecord {
    /**
     * Create a grid record object from the parameters provided.
     * @param {object} propRecord
     * @param {object} parentMVC
     * @param {object} clientRecord
     * @class
     * @classdesc Grid Record
     */
    constructor (propRecord, parentMVC, clientRecord) {
        this._ = {};
        this._.subs = {};
        this._.runSubCallbacks = (dataForCallback, subscriptionEventType) => {
            let ret = false;
            if (this._.subs.hasOwnProperty(subscriptionEventType)) {
                let arr = this._.subs[subscriptionEventType];
                arr.forEach((sub) => {
                    let r = sub[1](dataForCallback);
                    if (r) {
                        ret = true;
                    }
                });
            }
            return ret;
        };

        this.typeName = "datavoke.entities.gridRecord";

        this.clientRecord = clientRecord;
        this.parentMVC = parentMVC;
        this.isSelected = false;
        this.gridValues = new tools.dictionary();

        //TODO: Is this for a future state or can this be removed?..WR
        // Call the initial status calculation on construction...AL
        // if (this.clientRecord.Record.Status === null) this.status = app.dv.cache.valueStatuses.notInitializedStatus;
        // else this.status = app.dv.cache.valueStatuses.valueStatusByValue.getVal(this.clientRecord.Record.Status);

        // At this point all the values should be in ordered format, and there will be nulls for values that do not exist (will be handled in the gridValue creation)...AL
        for (let i = 0, il = propRecord.Values.count(); i < il; i++) {
            let val = propRecord.Values.Values[i],
                pi = propRecord.Values.Keys[i],
                pUID = propRecord.Values.KeysUID[i],
                clientValue = this.clientRecord.clientValues.getVal(pi),
                newGV = new GridValue.GridValue(clientValue.dynamicValue, pi, this);
            newGV.colTextFormatObj = val.colTextFormatObj;
            delete val.colTextFormatObj;
            // ensure that the client value knows about this new grid value so that updates may be pushed when a save occurs...WR
            clientValue.localValues.push(newGV);
            newGV.clientValue = clientValue;
            this.gridValues.add(pi, newGV, pUID);
        }

        // verify there are no other references for this record ID in this MVC and then add this reference to the client record local references array.
        let check = null;
        if (clientRecord != null) {
            check = addClientRecordReference(this);
            this.clientRecord.localRecords.push(this);
            if (Array.isArray(check)) {
                for (let i = check.length - 1; i >= 0; i--) {
                    check[i].dispose();
                    check.splice(i, 1);
                }
            }
        }

        /**
         * Flag to indicate if this object has been disposed.
         * @type {boolean}
         */
        this.isDisposed = false;
    }

    subscribe (event, src, callback) {
        tools.subscribe(this._.subs, event, src, callback);
    }

    unSubscribe (event, src) {
        tools.unSubscribe(this._.subs, event, src);
    }

    createLinkShell () {
        return createLinkShell(this);
    }

    get status () {
        return getRecordStatus(this);
    }
    set status (newStatus) {
        setRecordStatus(this, newStatus);
    }

    getStatus () {
        return getRecordStatus(this);
    }
    setStatus (newStatus) {
        setRecordStatus(this, newStatus);
    }

    /**
     * This function is called by a grid value when its value changes (saved change).
     */
    valueChangeGV (gv) {
        updateUI(this, gv);
    }

    // Calls the static updateUI...AL
    updateUI () {
        updateUI(this);
    }


    /**
     * Return if any grid values in this grid record are dirty. If so, then the
     * grid record is likewise dirty.
     */
    isDirty () {
        return isDirty(this);
    }

    /**
     * Get the record gridValue based off of property ID.
     * @/// <param name="propertyId" type="Integer">ID number of the property being searched for. The corresponding grid value object will be returned.</param>
     */
    getPropertyField (propertyId) {
        return this.gridValues.getVal(propertyId);
    }

    // this is to allow subsequent identification of the type of object...WR
    toString() {
        return "GridRecord";
    }

    /**
     * Setting a pivot grid value should create an unmodified value; pivot tables are not supposed
     * to be saved and therefore should not show fields as dirty when being set.
     */
    setPivotGridValue (value, pivotPropertyID) {
        return setPivotGridValue(this, value, pivotPropertyID);
    }

    setPivotRecordsForValue (records, pivotPropertyID) {
        return setPivotRecordsForValue(this, records, pivotPropertyID);
    }

    calculateGridValue (propertyIDs, formula) {
        return calculateGridValue(this, propertyIDs, formula);
    }

    dispose () {
        dispose(this);
    }

}

// Function to generate a new record
export function create (propRecord, parentMVC, clientRecord) {
    return new GridRecord(propRecord, parentMVC, clientRecord);
}

